import { IDataSourceConditions, IDataSourceInitConfig } from "@multiweb/reactCore/datasource";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { IOrderBy } from "@multiweb/types/multiweb";
import {
    HANDLE_RECORD_CLICK,
    INIT_FRAME,
    INIT_FRAME_WITH_DATASOURCE,
    SET_ACTION_AREA,
    SET_ALLOW_MULTISELECT,
    SET_CAN_VALUE_LOAD,
    SET_EMPTY_MESSAGE_CUSTOMIZATION,
    SET_INTERACTIVE,
    SET_LOAD_ON_SCROLL,
    SET_MULTISELECT_MODE,
    SET_MULTIWEB_VISIBILITY,
    SET_ROW_TEMPLATE,
    SET_SELECTED_RECORDS,
    SET_SHOW_HEADER,
    TRY_SELECT_RECORD,
} from "./constants";
import {
    ActionArea,
    EmptyMessageCustomization,
    IHandleRecordClickAction,
    IInitFrameAction,
    IInitFrameWithDataSourceAction,
    InitFrameConfig,
    ISetActionAreaAction,
    ISetAllowMultiselectAction,
    ISetCanValueLoadAction,
    ISetEmptyMessageCustomizationAction,
    ISetInteractiveAction,
    ISetLoadOnScrollAction,
    ISetMultiselectModeAction,
    ISetMultiwebVisibilityAction,
    ISetRowTemplateAction,
    ISetSelectedRecordsAction,
    ISetShowHeaderAction,
    ITrySelectRecordAction,
    RowTemplateType,
} from "./types";

/**
 * Akce pro inicializaci framu.
 *
 * @param frameKey Unikátní klíč pro frame.
 * @param initialCanValueLoad Počáteční canValueLoad framu.
 * @param initialInteractive Počáteční interactive framu.
 * @param frameConfig Konfigurační objekt použitý při inicializaci framu.
 * @param frameConfig.rowTemplate Šablona řádku
 * @param frameConfig.initialActionArea Počáteční oblast, do které se mají akce vykreslit.
 * @param frameConfig.initialShowHeader Určuje, zda se má zobrazit hlavička.
 * @param frameConfig.initialLoadOnScroll Určuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části.
 * @param frameConfig.emptyMessageCustomization Customizace ikony nebo nebo textu, pokud je frame prázdný.
 */
export const createInitFrameAction = (
    frameKey: string,
    initialCanValueLoad: boolean,
    initialInteractive: boolean,
    frameConfig: InitFrameConfig = EMPTY_OBJECT,
): IInitFrameAction => ({
    type: INIT_FRAME,
    payload: {
        initialCanValueLoad,
        initialInteractive,
        frameConfig,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce pro inicializaci framu, který využívá dataSource.
 *
 * @param frameKey Unikátní klíč pro frame.
 * @param initialCanValueLoad Počáteční canValueLoad framu.
 * @param initialInteractive Počáteční interactive framu.
 * @param dataSourceConfig Konfigurační objekt použitý při inicializaci datasource.
 * @param hasDetailForm Značí, zda je povoleno otevřít detail záznamu.
 * @param frameConfig Konfigurační objekt použitý při inicializaci framu.
 * @param frameConfig.rowTemplate Šablona řádku
 * @param frameConfig.initialActionArea Počáteční oblast, do které se mají akce vykreslit.
 * @param frameConfig.initialShowHeader Určuje, zda se má zobrazit hlavička.
 * @param frameConfig.initialLoadOnScroll Určuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části.
 * @param frameConfig.emptyMessageCustomization Customizace ikony nebo nebo textu, pokud je frame prázdný.
 */
export const createInitFrameWithDataSourceAction = (
    frameKey: string,
    initialCanValueLoad: boolean,
    initialInteractive: boolean,
    dataSourceConfig: IDataSourceInitConfig,
    hasDetailForm: boolean,
    frameConfig: InitFrameConfig = EMPTY_OBJECT,
    initialOrderBy: ReadonlyArray<IOrderBy> = EMPTY_ARRAY,
    initialConditions: Partial<IDataSourceConditions> = EMPTY_OBJECT,
): IInitFrameWithDataSourceAction => ({
    type: INIT_FRAME_WITH_DATASOURCE,
    payload: {
        initialCanValueLoad,
        initialInteractive,
        frameConfig,
        dataSourceConfig,
        hasDetailForm,
        initialOrderBy,
        initialConditions,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce pro zapnutí/vypnutí režimu výběru více záznamů.
 * Akce se odchytává v sáze {@link setMultiselectSaga} z modulu frames, která nastavuje
 * výběr více záznamů v datasource.
 * @see {@link createSetDatasourceMultiselectModeAction}
 * @param frameKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param multiselectMode Značí, zda je zapnut výběr více záznamů.
 */
export const createSetMultiselectModeAction = (
    frameKey: string,
    multiselectMode: boolean,
): ISetMultiselectModeAction => ({
    type: SET_MULTISELECT_MODE,
    payload: {
        multiselectMode,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce pro výběr záznamů daných identifikátory.
 *
 * @param frameKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param selectedRecordsIds Pole identifikátorů.
 */
export const createSetSelectedRecordsAction = (
    frameKey: string,
    selectedRecordsIds: ReadonlyArray<string>,
): ISetSelectedRecordsAction => ({
    type: SET_SELECTED_RECORDS,
    payload: {
        selectedRecordsIds,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce pro výběr záznamu daného identifikátorem.
 * Pokud záznam není označen, oznáčí ho.
 * Pokud záznam je označen, nic neprovede.
 *
 * @param frameKey Klíč pro identifikaci DataSource zadaný při inicializaci.
 * @param recordId Identifikátor záznamu, který má být označen.
 */
export const createTrySelectRecordAction = (frameKey: string, recordId: string): ITrySelectRecordAction => ({
    type: TRY_SELECT_RECORD,
    payload: {
        recordId,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je možné načítat další data.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param value Značí, zdali je možné načítat.
 */
export const createSetCanValueLoadAction = (frameKey: string, value: boolean): ISetCanValueLoadAction => ({
    type: SET_CAN_VALUE_LOAD,
    payload: {
        value,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje template, pomocí kterého se vykreslí DataRow.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param rowTemplate Nový RowTemplate.
 */
export const createSetRowTemplateAction = (frameKey: string, rowTemplate: RowTemplateType): ISetRowTemplateAction => ({ //TODO: zrušit?
    type: SET_ROW_TEMPLATE,
    payload: {
        rowTemplate,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje oblast, do které se zobrazují akce v DataRow.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param actionArea Nová ActionArea.
 */
export const createSetActionAreaAction = (frameKey: string, actionArea: ActionArea): ISetActionAreaAction => ({
    type: SET_ACTION_AREA,
    payload: {
        actionArea,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda se má zobrazit hlavička.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param showHeader Značí, zda se má zobrazit hlavička.
 */
export const createSetShowHeaderAction = (frameKey: string, showHeader: boolean): ISetShowHeaderAction => ({
    type: SET_SHOW_HEADER,
    payload: {
        showHeader,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param loadOnScroll Značí, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části.
 */
export const createSetLoadOnScrollAction = (frameKey: string, loadOnScroll: boolean): ISetLoadOnScrollAction => ({
    type: SET_LOAD_ON_SCROLL,
    payload: {
        loadOnScroll,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je frame interaktivní, zda s ním lze pracovat a pouštět nad ním akce.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param value Značí, zdali je možné s framem pracovat.
 */
export const createSetInteractiveAction = (frameKey: string, value: boolean): ISetInteractiveAction => ({
    type: SET_INTERACTIVE,
    payload: {
        value,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda může být zapnut režim výběru více záznamů.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param allowMultiselect Značí, zda je zapnut výběr více záznamů.
 */
export const createSetAllowMultiselectAction = (
    frameKey: string,
    allowMultiselect: boolean,
): ISetAllowMultiselectAction => ({
    type: SET_ALLOW_MULTISELECT,
    payload: {
        allowMultiselect,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce pro odchycení kliknutí na záznam.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param recordId Identifikátor záznamu.
 */
export const createHandleRecordClickAction = (frameKey: string, recordId: string): IHandleRecordClickAction => ({
    type: HANDLE_RECORD_CLICK,
    payload: {
        recordId,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je frame viditelný.
 * Využívá se např. v mixinu hasVisibilityChanged.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param multiwebVisibility Značí, zda je frame viditelný.
 */
export const createSetVisibilityAction = (
    frameKey: string,
    multiwebVisibility: boolean,
): ISetMultiwebVisibilityAction => ({
    type: SET_MULTIWEB_VISIBILITY,
    payload: {
        multiwebVisibility,
    },
    meta: {
        frameKey,
    },
});

/**
 * Akce, která nastavuje informaci o tom, zda je frame viditelný.
 * Využívá se např. v mixinu hasVisibilityChanged.
 *
 * @param frameKey Klíč pro identifikaci framu zadaný při inicializaci.
 * @param multiwebVisibility Značí, zda je frame viditelný.
 */
export const createSetEmptyMessageCustomizationAction = (
    frameKey: string,
    emptyMessageCustomization: EmptyMessageCustomization,
): ISetEmptyMessageCustomizationAction => ({
    type: SET_EMPTY_MESSAGE_CUSTOMIZATION,
    payload: {
        emptyMessageCustomization,
    },
    meta: {
        frameKey,
    },
});
